/* eslint-disable consistent-return */
import {
  LOGIN_MUTATION,
  LOGOUT_MUTATION,
  REQUEST_PHONE_CHANGE_MUTATION,
  RESEND_PHONE_CHANGE_CODE_MUTATION,
  RESET_PASSWORD_MUTATION,
  RESET_PASSWORD_REQUEST_MUTATION,
  SEND_2FA_CODE_TOKEN_MUTATION,
  Session,
  SESSION_QUERY,
  SuperAdmin,
  SUPERADMIN_SET_2FA_PHONE_MUTATION,
  USER_FRAGMENT,
  UserType,
  VERIFY_2FA_CODE_TOKEN_MUTATION,
  VERIFY_PHONE_CHANGE_MUTATION,
  VerifyPhoneChangeInput
} from '@graphql';
import { store } from '@store';
import { alpha2ToPhoneCountryCode, graphqlApi, paths } from '@utils';
import { initializeApp } from 'firebase/app';
import { getAuth, signInWithEmailAndPassword } from 'firebase/auth';
import { flow, makeAutoObservable } from 'mobx';
import { parsePhoneNumber } from 'react-phone-number-input';
import { toast, TypeOptions } from 'react-toastify';
import { AuthErrorCode } from '../../common/errors/auth-error-code.enum';
import { DEFAULT_HTTP_RESPONSE, handleMutationResponse } from './defaults';

const fConfig = {
  apiKey: process.env.REACT_APP_FB_K,
  authDomain: process.env.REACT_APP_FB_AD,
  projectId: process.env.REACT_APP_FB_PID,
  storageBucket: process.env.REACT_APP_FB_SB,
  messagingSenderId: process.env.REACT_APP_FB_MSI,
  appId: process.env.REACT_APP_FB_ID,
};

export class UserStore {
  userInfo: Session;

  isAuth = false;

  signInResponse: HttpResponse = DEFAULT_HTTP_RESPONSE;

  sessionResponse: HttpResponse = DEFAULT_HTTP_RESPONSE;

  resetPasswordResponse: HttpResponse = DEFAULT_HTTP_RESPONSE;

  setSuperadminPhoneResponse: HttpResponse = DEFAULT_HTTP_RESPONSE;

  twoFactorAuthResponse: HttpResponse = DEFAULT_HTTP_RESPONSE;

  phoneChangeResponse: HttpResponse = DEFAULT_HTTP_RESPONSE;

  twoFactorAuthState = {
    phoneNumber: '',
    countryCode: '',
    maskedPhoneNumber: '',
    codeExpiresAt: null as number | null,
    firebaseToken: '',
    email: '',
    userType: UserType.SuperAdmin,
    returnedCode: null as string | null,
  };

  app: any;

  constructor() {
    makeAutoObservable(this);
    this.app = initializeApp(fConfig);
    // Subscribe to auth state changes
    //  const auth = getAuth();
    //  onAuthStateChanged(auth, (user) => {
    //    if (!user) {
    //     this.clearSession();
    //    }
    //  });
  }

  createSession = () => {
    this.isAuth = true;
  };

  clearSession = () => {
    this.userInfo = {} as any;
    this.isAuth = false;
  };

  logOut = flow(function* logOut(this: UserStore): any {
    try {
      yield graphqlApi(LOGOUT_MUTATION);
    } catch (error) {
      toast(`Logout error: ${error}`, { type: 'error' });
    } finally {
      this.clearSession();
      store.historyStore.history.push(paths.login);
    }
  }).bind(this);

  logIn = flow(function* logIn(
    this: UserStore,
    variables: { email: string; password: string; type: UserType },
  ): any {
    try {
      this.signInResponse.status = 'pending';
      const tokenResponse = yield this.signIn(variables.email, variables.password);
      if (tokenResponse) {
        const userResponse = yield graphqlApi(LOGIN_MUTATION, {
          email: variables.email,
          token: tokenResponse,
          type: variables.type,
        });

        // Check for 2FA requirement error
        const errorCode = userResponse?.data?.errors?.[0]?.extensions?.code;
        const requires2FARegistration = errorCode === AuthErrorCode.SuperAdmin2faNotEnabled;
        const isMissingOrInvalidDeviceId = errorCode === AuthErrorCode.MissingOrInvalidDeviceId;

        if (requires2FARegistration || isMissingOrInvalidDeviceId) {
          // Store Firebase token, email, and userType for 2FA flow
          this.twoFactorAuthState.firebaseToken = tokenResponse;
          this.twoFactorAuthState.email = variables.email;
          this.twoFactorAuthState.userType = variables.type;
          // Throw error so LoginPage can catch and redirect
          throw new Error(errorCode);
        }

        this.signInResponse.status = 'ok';
        this.userInfo = userResponse?.data?.data?.login;

        if (this.userInfo) {
          this.createSession();
          toast('User logged in.', { type: 'success' });
        } else {
          toast('Invalid email or password.', { type: 'info' });
        }
        return userResponse;
      }
    } catch (err: any) {
      let toastMessage = 'Something went wrong!';
      let toastType: TypeOptions = 'error';
      if (err?.message === AuthErrorCode.SuperAdmin2faNotEnabled) {
        toastMessage = 'Two-factor authentication must be enabled before accessing this resource.';
        toastType = 'info';
      }

      if (err?.message === AuthErrorCode.MissingOrInvalidDeviceId) {
        toastMessage = 'Device not trusted. Please verify your device.';
        toastType = 'info';
      }

      toast(toastMessage, { type: toastType });
      this.signInResponse.status = 'badRequest';
      this.signInResponse.errMessage = 'Bad Request';
      throw err;
    }
  }).bind(this);

  signIn = flow(function* signIn(email, password): any {
    try {
      const auth = getAuth();
      const userCredential = yield signInWithEmailAndPassword(auth, email, password);
      const { user } = userCredential;
      return new Promise((resolve, reject) => {
        user
          .getIdToken()
          .then((token: any) => {
            // Additional logic after signing in the user
            resolve(token);
          })
          .catch((error: any) => {
            // get token error
            reject(error);
          });
      });
    } catch (error) {
      // Handle error
      throw error;
    }
  });

  /**
   * Loads the signed-in user. Page data (organizations, advisors, settings) has its own queries in
   * the other stores, so the session stays small and is loaded once per page load.
   */
  getCurrentSession = flow(function* getCurrentSession(this: UserStore): any {
    try {
      this.sessionResponse.status = 'pending';
      const userResponse = yield graphqlApi(SESSION_QUERY(USER_FRAGMENT()));

      this.sessionResponse.status = 'ok';

      if (userResponse?.data?.errors) {
        return this.clearSession();
      }

      this.userInfo = {
        auth: {
          ...this.userInfo?.auth,
          ...userResponse?.data?.data?.currentSession?.auth,
        },
        user: {
          ...this.userInfo?.user,
          ...userResponse?.data?.data?.currentSession?.user,
        },
      };

      if (this.userInfo) this.createSession();
      else this.clearSession();
      return this.userInfo;
    } catch (err) {
      this.sessionResponse.status = 'badRequest';
      this.sessionResponse.errMessage = 'Bad Request';
    }
  }).bind(this);

  resetPasswordRequest = flow(function* resetPasswordRequest(
    this: UserStore,
    variables: { type: UserType; email: string; recaptchaToken: string },
  ): any {
    try {
      this.resetPasswordResponse.status = 'pending';
      const userResponse = yield graphqlApi(RESET_PASSWORD_REQUEST_MUTATION, variables);
      this.resetPasswordResponse.status = 'ok';

      const data = handleMutationResponse(
        userResponse,
        'requestResetPassword',
      );

      return data;
    } catch (err) {
      toast('Something went wrong!', { type: 'error' });
      this.resetPasswordResponse.status = 'badRequest';
      this.resetPasswordResponse.errMessage = 'Bad Request';
      throw err;
    }
  }).bind(this);

  resetPassword = flow(function* resetPassword(
    this: UserStore,
    variables: { tokenId: string; userId: string; password: string; email?: string },
  ): any {
    try {
      this.resetPasswordResponse.status = 'pending';
      const userResponse = yield graphqlApi(RESET_PASSWORD_MUTATION, variables);
      this.resetPasswordResponse.status = 'ok';
      const response = userResponse?.data?.data?.resetPasswordAndEmail;
      if (response) {
        toast('Password reset successful.', { type: 'success' });
        store.historyStore.history.push(paths.login);
      } else {
        toast('Invalid credentials.', { type: 'info' });
      }
      return userResponse;
    } catch (err) {
      toast('Something went wrong!', { type: 'error' });
      this.resetPasswordResponse.status = 'badRequest';
      this.resetPasswordResponse.errMessage = 'Bad Request';
    }
  }).bind(this);

  get user(): SuperAdmin {
    const superAdmin = this.userInfo.user as SuperAdmin;

    return superAdmin || {};
  }

  setSuperadminPhone = flow(function* setSuperadminPhone(
    this: UserStore,
    variables: { phone: string; countryCodeAlpha2: string },
  ): any {
    const token = this.twoFactorAuthState.firebaseToken;
    if (!token) {
      toast('Session expired. Please sign in again.', { type: 'error' });
      this.setSuperadminPhoneResponse.status = 'badRequest';
      return;
    }

    const { phone, countryCodeAlpha2 } = variables;
    try {
      this.setSuperadminPhoneResponse.status = 'pending';
      const phoneCountryCode = alpha2ToPhoneCountryCode(countryCodeAlpha2);
      if (!phoneCountryCode) {
        toast('Unsupported country for 2FA.', { type: 'error' });
        this.setSuperadminPhoneResponse.status = 'badRequest';
        return;
      }
      const response = yield graphqlApi(SUPERADMIN_SET_2FA_PHONE_MUTATION, {
        token,
        phone,
        phoneCountryCode,
      });
      this.setSuperadminPhoneResponse.status = 'ok';
      const result = response?.data?.data?.superadminSet2FAPhone;
      const success = result && typeof result === 'object' ? result.success : Boolean(result);

      if (success) {
        this.twoFactorAuthState.phoneNumber = phone;
        this.twoFactorAuthState.countryCode = countryCodeAlpha2;
        this.twoFactorAuthState.returnedCode =
          typeof result === 'object' && result?.code ? result.code : null;

        const parsed = parsePhoneNumber(phone);
        if (parsed) {
          const { nationalNumber } = parsed;
          this.twoFactorAuthState.maskedPhoneNumber = nationalNumber.replace(
            /(\d{3})(\d{3})(\d{4})/,
            '(***) ***-$3',
          );
        } else {
          this.twoFactorAuthState.maskedPhoneNumber = '(***) ***-****';
        }

        this.twoFactorAuthState.codeExpiresAt = Date.now() + 600 * 1000;
        toast('Verification code sent successfully.', { type: 'success' });
      } else {
        toast('Failed to send verification code.', { type: 'error' });
      }
      return success;
    } catch (err) {
      toast('Failed to send verification code.', { type: 'error' });
      this.setSuperadminPhoneResponse.status = 'badRequest';
      this.setSuperadminPhoneResponse.errMessage = 'Bad Request';
      throw err;
    }
  }).bind(this);

  send2FACode = flow(function* send2FACode(this: UserStore): any {
    const token = this.twoFactorAuthState.firebaseToken;
    if (!token) {
      toast('Session expired. Please sign in again.', { type: 'error' });
      this.twoFactorAuthResponse.status = 'badRequest';
      return;
    }

    try {
      this.twoFactorAuthResponse.status = 'pending';
      const response = yield graphqlApi(SEND_2FA_CODE_TOKEN_MUTATION, {
        type: this.twoFactorAuthState.userType,
        token,
      });
      this.twoFactorAuthResponse.status = 'ok';
      const result = response?.data?.data?.send2faCode;
      if (result?.success) {
        this.twoFactorAuthState.returnedCode = result.code ?? null;
        this.twoFactorAuthState.codeExpiresAt = Date.now() + 600 * 1000;
        toast('Verification code sent successfully.', { type: 'success' });
        return true;
      }
      toast('Failed to send verification code.', { type: 'error' });
      return false;
    } catch (err) {
      toast('Failed to send verification code.', { type: 'error' });
      this.twoFactorAuthResponse.status = 'badRequest';
      this.twoFactorAuthResponse.errMessage = 'Bad Request';
      throw err;
    }
  }).bind(this);

  verify2FACode = flow(function* verify2FACode(this: UserStore, code: string): any {
    const { firebaseToken, userType, email } = this.twoFactorAuthState;

    try {
      this.twoFactorAuthResponse.status = 'pending';
      const response = yield graphqlApi(VERIFY_2FA_CODE_TOKEN_MUTATION, {
        type: userType,
        token: firebaseToken,
        code,
      });
      this.twoFactorAuthResponse.status = 'ok';
      const verified = response?.data?.data?.verify2faCode;
      if (verified === true) {
        const loginResponse = yield graphqlApi(LOGIN_MUTATION, {
          type: userType,
          email,
          token: firebaseToken,
        });
        const loginData = loginResponse?.data?.data?.login;
        if (loginData?.user && loginData?.auth) {
          this.userInfo = { user: loginData.user, auth: loginData.auth };
          this.createSession();
          this.clearTwoFactorAuthState();
          toast('Device verified successfully.', { type: 'success' });
          return loginData;
        }
      }
      toast('Invalid verification code.', { type: 'error' });
      throw new Error('Invalid code');
    } catch (err) {
      toast('Failed to verify code.', { type: 'error' });
      this.twoFactorAuthResponse.status = 'badRequest';
      this.twoFactorAuthResponse.errMessage = 'Bad Request';
      throw err;
    }
  }).bind(this);

  clearTwoFactorAuthState = () => {
    this.twoFactorAuthState = {
      phoneNumber: '',
      countryCode: '',
      maskedPhoneNumber: '',
      codeExpiresAt: null,
      firebaseToken: '',
      email: '',
      userType: UserType.SuperAdmin,
      returnedCode: null,
    };
  };

  /** SuperAdmin only: request phone change; sends 6-digit code to new number. Session-based. */
  requestPhoneChange = flow(function* requestPhoneChange(
    this: UserStore,
    variables: { phone: string; countryCodeAlpha2: string },
  ): any {
    const { phone, countryCodeAlpha2 } = variables;
    const phoneCountryCode = alpha2ToPhoneCountryCode(countryCodeAlpha2);
    if (!phoneCountryCode) {
      toast('Unsupported country.', { type: 'error' });
      this.phoneChangeResponse.status = 'badRequest';
      return false;
    }
    try {
      this.phoneChangeResponse.status = 'pending';
      const response = yield graphqlApi(REQUEST_PHONE_CHANGE_MUTATION, {
        phone,
        phoneCountryCode,
      });
      this.phoneChangeResponse.status = 'ok';
      const success = Boolean(response?.data?.data?.requestPhoneChange);
      if (success) {
        toast('Verification code sent.', { type: 'success' });
      } else {
        toast('Failed to send verification code.', { type: 'error' });
      }
      return success;
    } catch (err) {
      toast('Failed to send verification code.', { type: 'error' });
      this.phoneChangeResponse.status = 'badRequest';
      this.phoneChangeResponse.errMessage = 'Bad Request';
      throw err;
    }
  }).bind(this);

  /** SuperAdmin only: verify code and apply phone + profile. Requires pending phone change. */
  verifyPhoneChange = flow(function* verifyPhoneChange(
    this: UserStore,
    input: VerifyPhoneChangeInput,
  ): any {
    try {
      this.phoneChangeResponse.status = 'pending';
      const response = yield graphqlApi(VERIFY_PHONE_CHANGE_MUTATION, {
        input: {
          code: input.code,
          phone: input.phone,
          phoneCountryCode: input.phoneCountryCode,
        },
      });
      this.phoneChangeResponse.status = 'ok';
      const success = Boolean(response?.data?.data?.verifyPhoneChange);
      if (success) {
        toast('Phone updated.', { type: 'success' });
        yield this.getCurrentSession();
      } else {
        toast('Invalid code or phone update failed.', { type: 'error' });
      }
      return success;
    } catch (err) {
      toast('Invalid code or update failed.', { type: 'error' });
      this.phoneChangeResponse.status = 'badRequest';
      this.phoneChangeResponse.errMessage = 'Bad Request';
      throw err;
    }
  }).bind(this);

  /** SuperAdmin only: resend verification code to the given number. Session-based. */
  resendPhoneChangeCode = flow(function* resendPhoneChangeCode(
    this: UserStore,
    variables: { phone: string; countryCodeAlpha2: string },
  ): any {
    const { phone, countryCodeAlpha2 } = variables;
    const phoneCountryCode = alpha2ToPhoneCountryCode(countryCodeAlpha2);
    if (!phoneCountryCode) {
      toast('Unsupported country.', { type: 'error' });
      this.phoneChangeResponse.status = 'badRequest';
      return false;
    }
    try {
      this.phoneChangeResponse.status = 'pending';
      const response = yield graphqlApi(RESEND_PHONE_CHANGE_CODE_MUTATION, {
        phone,
        phoneCountryCode,
      });
      this.phoneChangeResponse.status = 'ok';
      const success = Boolean(response?.data?.data?.resendPhoneChangeCode);
      if (success) {
        toast('Verification code sent.', { type: 'success' });
      } else {
        toast('Failed to resend code.', { type: 'error' });
      }
      return success;
    } catch (err) {
      toast('Failed to resend code.', { type: 'error' });
      this.phoneChangeResponse.status = 'badRequest';
      this.phoneChangeResponse.errMessage = 'Bad Request';
      throw err;
    }
  }).bind(this);
}
